import React from 'react';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';

const Stepper = ({ 
  steps = [], 
  currentStep = 0, 
  className = '' 
}) => {
  return (
    <div 
      className={`flex flex-col gap-6 relative pl-3 ${className}`}
      role="list"
      aria-label="Order progress tracker"
    >
      {/* Connector track bar */}
      <div 
        className="absolute left-6 top-3 bottom-3 w-1 bg-slate-100 dark:bg-slate-700/50 rounded-full z-0"
        aria-hidden="true"
      >
        <div 
          className="w-full bg-cyan-500 rounded-full transition-all duration-700" 
          style={{ height: `${steps.length > 1 ? (Math.max(0, currentStep) / (steps.length - 1)) * 100 : 0}%` }}
        />
      </div>

      {steps.map((step, idx) => {
        const isCompleted = idx < currentStep;
        const isActive = idx === currentStep;
        const isUpcoming = idx > currentStep;

        let statusText = 'Upcoming';
        if (isCompleted) statusText = 'Completed';
        if (isActive) statusText = 'Current step';

        return (
          <div 
            key={idx} 
            className="flex gap-4 relative z-10 items-start"
            role="listitem"
            aria-current={isActive ? 'step' : undefined}
          >
            {/* Step circle indicator */}
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                isCompleted 
                  ? 'bg-cyan-500 border-cyan-500 text-white shadow-md shadow-cyan-500/10' 
                  : isActive
                    ? 'bg-white dark:bg-slate-800 border-cyan-500 text-cyan-600 shadow-sm scale-110'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400'
              }`}
              aria-hidden="true"
            >
              {isCompleted ? <Check size={16} strokeWidth={3} /> : step.icon}
            </div>

            {/* Step text context */}
            <div className="min-w-0 flex-1">
              <span className="sr-only">{`Step ${idx + 1}: ${statusText}. `}</span>
              <h4 className={`font-bold text-sm ${
                isActive ? 'text-slate-900 dark:text-slate-50' : isCompleted ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'
              }`}>
                {step.label}
              </h4>
              {step.desc && (
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{step.desc}</p>
              )}
              {step.time && (
                <p className="text-[10px] font-bold text-cyan-600 dark:text-cyan-500 mt-1 flex items-center gap-1">
                  <span>⏱</span>
                  <span>{step.time}</span>
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      desc: PropTypes.string,
      icon: PropTypes.node.isRequired,
      time: PropTypes.string
    })
  ).isRequired,
  currentStep: PropTypes.number.isRequired,
  className: PropTypes.string
};

export default Stepper;
